import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BankAccount } from '@accounting/shared';
import { api } from '../lib/api';

// Mirror of GlobalPlaidAccount / GlobalPlaidItem in apps/api/src/services/bank.service.ts.
export interface GlobalPlaidAccount {
  plaidAccountId: string;
  name: string;
  mask: string | null;
  type: string | null;
  subtype: string | null;
  linkedBankAccountId: string | null;
  linkedCompanyId: string | null;
  linkedCompanyName: string | null;
  linkedGlAccountId: string | null;
  linkedGlAccountCode: string | null;
  linkedGlAccountName: string | null;
}

export interface GlobalPlaidItem {
  plaidItemId: string;
  institutionName: string | null;
  representativeBankAccountId: string;
  accounts: GlobalPlaidAccount[];
  unreachable: boolean;
  unreachableReason: string | null;
  /** Bank wants a fresh sign-in (new MFA, changed password). Reconnect on the SAME Item. */
  needsLogin: boolean;
}

export const mePlaidItemsKey = ['me', 'plaid-items'] as const;

export function useMePlaidItems() {
  return useQuery({
    queryKey: mePlaidItemsKey,
    queryFn: () =>
      api.get<{ items: GlobalPlaidItem[] }>(`/me/plaid-items`).then((r) => r.items),
    // Don't auto-refetch on focus — each call hits Plaid /accounts/get which is
    // rate-limited per Item. The user can refresh manually if needed.
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });
}

export function useLinkPlaidAccountToCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      plaidItemId,
      plaidAccountId,
      companyId,
      glAccountId,
    }: {
      plaidItemId: string;
      plaidAccountId: string;
      companyId: string;
      glAccountId: string;
    }) =>
      api
        .post<{ bankAccount: BankAccount }>(
          `/me/plaid-items/${plaidItemId}/accounts/${plaidAccountId}/link`,
          { companyId, glAccountId },
        )
        .then((r) => r.bankAccount),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: mePlaidItemsKey });
      qc.invalidateQueries({ queryKey: ['bank-accounts'] });
    },
  });
}

export function useRemapPlaidMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      bankAccountId,
      companyId,
      glAccountId,
    }: {
      bankAccountId: string;
      companyId: string;
      glAccountId: string;
    }) =>
      api
        .patch<{ bankAccount: BankAccount }>(`/me/plaid-mappings/${bankAccountId}`, {
          companyId,
          glAccountId,
        })
        .then((r) => r.bankAccount),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: mePlaidItemsKey });
      qc.invalidateQueries({ queryKey: ['bank-accounts'] });
    },
  });
}

export function useUnlinkPlaidMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (bankAccountId: string) =>
      api
        .delete<{ bankAccount: BankAccount }>(`/me/plaid-mappings/${bankAccountId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: mePlaidItemsKey });
      qc.invalidateQueries({ queryKey: ['bank-accounts'] });
    },
  });
}

export interface SyncAllResult {
  itemsSucceeded: number;
  itemsFailed: number;
  rowsTouched: number;
  callsToPlaid: number;
  added: number;
  modified: number;
  removed: number;
  errors: string[];
}

/**
 * Trigger a sync of every active Plaid Item across all companies. Backs the
 * "Sync All" sidebar button. Invalidates bank queries on success so transaction
 * counts/lists refresh immediately.
 */
export function useSyncAllPlaid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<{ result: SyncAllResult }>(`/me/plaid-items/sync-all`, {}).then((r) => r.result),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bank-transactions'] });
      qc.invalidateQueries({ queryKey: ['bank-accounts'] });
      qc.invalidateQueries({ queryKey: mePlaidItemsKey });
    },
  });
}

export interface RemovePlaidItemResult {
  plaidItemId: string;
  /** False when Plaid did not confirm the delete — billing may still be live. */
  removedAtPlaid: boolean;
  rowsDeactivated: number;
  warning: string | null;
}

/**
 * Delete a whole Plaid connection at Plaid and retire its rows. This is what
 * actually stops Transactions subscription billing — unlinking a mapping does
 * not. Transactions and journal entries are untouched.
 */
export function useRemovePlaidItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (plaidItemId: string) =>
      api.delete<RemovePlaidItemResult>(`/me/plaid-items/${plaidItemId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: mePlaidItemsKey });
      qc.invalidateQueries({ queryKey: ['bank-accounts'] });
      qc.invalidateQueries({ queryKey: ['bank-transactions'] });
    },
  });
}

/**
 * Update-mode link token with Account Select, for granting accounts that did
 * not exist (or went unchecked) when the bank was first connected.
 */
export function useItemUpdateLinkToken() {
  return useMutation({
    mutationFn: (plaidItemId: string) =>
      api.post<{ linkToken: string; expiration: string }>(
        `/me/plaid-items/${plaidItemId}/update-link-token`,
        { redirectUri: `${window.location.origin}/plaid` },
      ),
  });
}

/**
 * Rotate the Item's access_token at Plaid. No user re-authentication; the Item,
 * its transactions and its sync cursor all survive. Use after a credential
 * exposure, or for scheduled rotation.
 */
export function useRotatePlaidItemToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (plaidItemId: string) =>
      api.post<{ plaidItemId: string; rowsUpdated: number }>(
        `/me/plaid-items/${plaidItemId}/rotate-token`,
        {},
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: mePlaidItemsKey }),
  });
}

export function useUpgradeManualToPlaid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      manualBankAccountId,
      plaidItemId,
      plaidAccountId,
    }: {
      manualBankAccountId: string;
      plaidItemId: string;
      plaidAccountId: string;
    }) =>
      api
        .post<{ bankAccount: BankAccount }>(
          `/me/plaid-mappings/upgrade/${manualBankAccountId}`,
          { plaidItemId, plaidAccountId },
        )
        .then((r) => r.bankAccount),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: mePlaidItemsKey });
      qc.invalidateQueries({ queryKey: ['bank-accounts'] });
    },
  });
}
